import { useMemo, useSyncExternalStore } from "react";

/**
 * Subscribe helper for window.matchMedia queries.
 * Uses modern `addEventListener('change', ...)` when available,
 * and falls back to deprecated `addListener` for older Safari.
 */
function subscribeMedia(query: string, cb: () => void) {
  const mql = window.matchMedia(query);
  if (mql.addEventListener) mql.addEventListener("change", cb);
  else (mql as any).addListener?.(cb); // Safari < 14
  return () => {
    if (mql.removeEventListener) mql.removeEventListener("change", cb);
    else (mql as any).removeListener?.(cb);
  };
}

/**
 * Hook that mirrors the CSS media query `(max-width: {maxWidth}px)`.
 * This gives you a boolean that is consistent with your CSS breakpoint,
 * instead of relying on user-agent detection.
 *
 * SSR-safe via `useSyncExternalStore`.
 *
 * @param maxWidth - breakpoint in pixels, e.g. 900 to match `@media (max-width: 900px)`
 * @returns boolean — true if the viewport currently matches the query
 */
export function useIsMobile(maxWidth = 900) {
  const query = useMemo(() => `(max-width: ${maxWidth}px)`, [maxWidth]);

  // Current value in the browser
  const getSnapshot = () =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false;

  // Server-side snapshot (avoid hydration mismatches by assuming desktop)
  const getServerSnapshot = () => false;

  const subscribe = (onStoreChange: () => void) => {
    if (typeof window === "undefined") return () => {};
    return subscribeMedia(query, onStoreChange);
  };

  // useSyncExternalStore guarantees correctness across concurrent rendering + SSR
  const isMobile = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return isMobile;
}
